import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { compose } from 'utils/compose';

import { inject, observer } from 'mobx-react';
import { Dialog, DialogContent, Paper, Container, Grid, CircularProgress } from '@mui/material';

import DialogHeader from './DialogHeader';
import { DialogActionButton, DialogFooter } from 'components/Dialog';

import useSearchParams from 'hooks/useSearchParams';

const PREFIX = 'ShowAutoCorrectedAddressDialog';

const classes = {
  root: `${PREFIX}-root`,
  text: `${PREFIX}-text`,
  diff: `${PREFIX}-diff`,
  description: `${PREFIX}-description`,
  label: `${PREFIX}-label`,
  value: `${PREFIX}-value`,
  input: `${PREFIX}-input`,
  btn: `${PREFIX}-btn`,
  form: `${PREFIX}-form`,
  paperContainer: `${PREFIX}-paperContainer`,
  paperContainerSelected: `${PREFIX}-paperContainerSelected`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`&.${classes.root}`]: {},

  [`& .${classes.text}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
    marginTop: 0,
  },

  [`& .${classes.diff}`]: {
    color: theme.colorTokens.error,
    fontSize: '14px',
    fontStyle: 'italic',
  },

  [`& .${classes.description}`]: {
    color: theme.colorTokens.onSurfaceTextHeadline,
    fontSize: '14px',
    fontStyle: 'italic',
  },

  [`& .${classes.label}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
    fontWeight: 600,
  },

  [`& .${classes.value}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
  },

  [`& .${classes.input}`]: {
    fontSize: '12px',
  },

  [`& .${classes.btn}`]: {
    borderRadius: '2rem',
    marginLeft: '0 !important',
    whiteSpace: 'nowrap',
    minWidth: 'auto',
  },

  [`& .${classes.form}`]: {
    display: 'grid',
    gridAutoFlow: 'row',
    [theme.breakpoints.up('sm')]: {
      gridAutoFlow: 'column',
      gridTemplateColumns: '100px 1fr',
    },
  },

  [`& .${classes.paperContainer}`]: {
    padding: '12px 24px',
    '&:hover': {
      border: `1px solid ${theme.palette.secondary.main}`,
    },
  },

  [`& .${classes.paperContainerSelected}`]: {
    padding: '12px 24px',
    border: `2px solid ${theme.palette.secondary.main}`,
  },
}));

function ShowAutoCorrectedAddressDialog({ store }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const { deliveryStore } = store;
  const { delivery, loadingDelivery } = deliveryStore;
  const [loading, setLoading] = useState(false);
  const address = delivery?.shipment?.dropoff_address;

  const originalAddress = delivery?.incident?.extra?.originalAddress;
  const originalAddressStreet2 = address?.street2 || '';

  const addressComponents = delivery?.enriched_address?.enrichedAddress?.addressComponents;

  const handleClose = () => {
    searchParams.set('dialog', 'closed');
    setSearchParams(searchParams);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" className={classes.container}>
        <Grid style={{ height: 300 }} container alignItems="center" justifyContent="center">
          <CircularProgress color="primary" />
        </Grid>
      </Container>
    );
  }
  const showDiff = (str, orig) => {
    if (str === null || str === undefined) {
      //we will let empty strings through
      return <span className={classes.value}>{orig}</span>;
    }
    const isDiff = (str && !orig) || (orig && !str) || (str && orig && str.trim().toUpperCase() !== orig.trim().toUpperCase());
    if (!isDiff) {
      return <span className={classes.value}>{orig}</span>;
    } else {
      return <span className={classes.diff}>{!str ? 'N/A' : str}</span>;
    }
  };
  const showDiffZip = (addressComponents, orig) => {
    if (!addressComponents?.componentNotes?.zip && !addressComponents?.componentNotes?.zip4) {
      return <span className={classes.value}>{orig}</span>;
    }

    let zip5Class = classes.value;
    if (!!addressComponents?.componentNotes?.zip) {
      zip5Class = classes.diff;
    }

    let zip4 = '';
    if (orig.includes('-') && !!addressComponents?.zip4) {
      let zip4Class = classes.value;
      if (!!addressComponents?.componentNotes?.zip4) {
        zip4Class = classes.diff;
      }
      zip4 = <span className={zip4Class}>-{addressComponents?.zip4}</span>;
    }
    return (
      <>
        <span className={zip5Class}>{addressComponents.zip}</span>
        {zip4}
      </>
    );
  };

  if (loading || loadingDelivery) {
    return (
      <StyledDialog open fullWidth maxWidth="md" className={classes.root}>
        <Grid style={{ height: 300 }} container alignItems="center" justifyContent="center">
          <CircularProgress color="primary" />
        </Grid>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open maxWidth="md" fullWidth className={classes.root}>
      <DialogHeader title="Address Correction" />
      <DialogContent>
        <p className={classes.text}>
          We have corrected your address. Please contact <a href="mailto:support@gojitsu.com">support</a> to update
        </p>
        <Paper elevation={24} variant="outlined" className={classes.paperContainerSelected}>
          <p className={classes.description}>Corrected Address:</p>
          <div className={classes.form}>
            <p className={classes.label}>Address 1:</p>
            <p className={classes.value}>
              {showDiff(
                !!addressComponents?.componentNotes?.street1 ? addressComponents?.streetLine : originalAddress?.street,
                originalAddress?.street,
              )}
            </p>
          </div>
          <div className={classes.form}>
            <p className={classes.label}>Address 2:</p>
            <p className={classes.value}>
              {showDiff(
                !!addressComponents?.componentNotes?.street2 || !!addressComponents?.componentNotes?.secondaryAddressUnitDesignator
                  ? addressComponents?.secondaryAddressLine
                  : originalAddressStreet2,
                originalAddressStreet2,
              )}
            </p>
          </div>
          <div className={classes.form}>
            <p className={classes.label}>City:</p>
            <p className={classes.value}>
              {showDiff(!!addressComponents?.componentNotes?.city ? addressComponents?.city : originalAddress?.city, originalAddress?.city)}
            </p>
          </div>
          <div className={classes.form}>
            <p className={classes.label}>State:</p>
            <p className={classes.value}>
              {showDiff(!!addressComponents?.componentNotes?.state ? addressComponents?.state : originalAddress?.state, originalAddress?.state)}
            </p>
          </div>
          <div className={classes.form}>
            <p className={classes.label}>Zipcode:</p>
            <p className={classes.value}>{showDiffZip(addressComponents, originalAddress?.zipcode)}</p>
          </div>
        </Paper>
        {originalAddress?.street && (
          <Paper elevation={3} variant="outlined" className={classes.paperContainer} sx={{ mt: 1 }}>
            <p className={classes.description}>Original Address:</p>
            <div className={classes.form}>
              <p className={classes.label}>Address 1:</p>
              <p className={classes.value}>{originalAddress?.street || 'N/A'}</p>
            </div>
            <div className={classes.form}>
              <p className={classes.label}>Address 2:</p>
              <p className={classes.value}>{originalAddressStreet2 || 'N/A'}</p>
            </div>
            <div className={classes.form}>
              <p className={classes.label}>City:</p>
              <p className={classes.value}>{originalAddress?.city}</p>
            </div>
            <div className={classes.form}>
              <p className={classes.label}>State:</p>
              <p className={classes.value}>{originalAddress?.state}</p>
            </div>
            <div className={classes.form}>
              <p className={classes.label}>Zipcode:</p>
              <p className={classes.value}>{originalAddress?.zipcode}</p>
            </div>
          </Paper>
        )}
      </DialogContent>
      <DialogFooter>
        <DialogActionButton size="medium" variant="contained" color="primary" onClick={handleClose} disableElevation>
          Close
        </DialogActionButton>
        <DialogActionButton
          size="medium"
          variant="contained"
          color="info"
          onClick={() => {
            window.location.href = 'mailto:support@gojitsu.com';
            handleClose();
          }}
          disableElevation
        >
          Contact Support
        </DialogActionButton>
      </DialogFooter>
    </StyledDialog>
  );
}

export default compose(inject('store'), observer)(ShowAutoCorrectedAddressDialog);
